//@ts-ignore
import React, { useCallback, useEffect, useState } from 'react';
import { AudioPlayer, CloseButton, FullScreen, FullscreenButton, Iframe } from 'src/units';
import { SweepType } from 'src/_models/Sweep';

import parse from 'html-react-parser';
import { useBoolean } from 'usehooks-ts';

import ReactPlayer from 'react-player';
import { Popup, SweepPopup, SweepTextWrap } from './styled';
import { TGroup } from './types';
import { TSubtitle } from 'src/units/AudioPlayer/types';
import { useLoadModel } from 'src/hooks/useLoadModel';
import { Panorama } from '../panorama';

type TProps = {
  group?: TGroup;
  music?: {
    url: string;
    subtitles: TSubtitle[];
  };
  greenScreen?: {
    urls: { src: string; type: string }[];
  };
  sweepId?: string;
  showcase?: any;
  panoramas?: any[];
  awsUrl?: string;
  tour?: any;
};

export const Sweep = (props: TProps) => {
  const { group, music, greenScreen, sweepId, showcase, panoramas, awsUrl, tour } = props;

  const { loadModel, panorama } = useLoadModel(showcase);

  const [greenScreenPlayer, setGreenScreenPlayer] = useState({
    playing: false,
  });

  const {
    value: isTextDialogVisible,
    setTrue: showTextDialog,
    setFalse: closeTextDialog,
  } = useBoolean();

  const {
    value: isIframeDialogVisible,
    setTrue: showIframeDialog,
    setFalse: closeIframeDialog,
  } = useBoolean();

  const {
    value: isVideoDialogVisible,
    setTrue: showVideoDialog,
    setFalse: closeVideoDialog,
  } = useBoolean();

  const {
    value: isImageDialogVisible,
    setTrue: showImageDialog,
    setFalse: closeImageDialog,
  } = useBoolean();

  const {
    value: isGreenScreenDialogVisible,
    setTrue: showGreenScreenDialog,
    setFalse: closeGreenScreenDialog,
  } = useBoolean();

  const {
    value: isMusicPlayerVisible,
    setTrue: showMusicPlayer,
    setFalse: closeMusicPlayer,
  } = useBoolean();

  useEffect(() => {
    if (!group) {
      return;
    }
    if (group.type === SweepType.Tab) {
      window.open(group.url, '_blank');
    }
    if (group.type === SweepType.Panorama && group?.panoramaId) {
      loadModel({ panoramaId: group.panoramaId, type: SweepType.Panorama });
    }
    if (group.type === SweepType.Text) {
      showTextDialog();
    } else {
      closeTextDialog();
    }

    if (group.type === SweepType.Iframe) {
      showIframeDialog();
    } else {
      closeIframeDialog();
    }

    if (group.type === SweepType.Image) {
      showImageDialog();
    } else {
      closeImageDialog();
    }

    if (group.type === SweepType.Video) {
      showVideoDialog();
    } else {
      closeVideoDialog();
    }

    if (group.type === SweepType.GreenScreen) {
      showGreenScreenDialog();
    } else {
      closeGreenScreenDialog();
    }

    if (group.type === SweepType.Music) {
      showMusicPlayer();
    } else {
      closeMusicPlayer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    group,
    sweepId,
    showTextDialog,
    closeTextDialog,
    showIframeDialog,
    closeIframeDialog,
    showVideoDialog,
    closeVideoDialog,
    showImageDialog,
    closeImageDialog,
    showGreenScreenDialog,
    closeGreenScreenDialog,
    showMusicPlayer,
    closeMusicPlayer,
  ]);

  const renderDialogContent = useCallback(
    (group: TGroup) => {
      switch (group.type) {
        case SweepType.Text:
          return (
            <SweepPopup>
              <CloseButton onClick={closeTextDialog} isFullscreen={false} />
              <SweepTextWrap>{parse(group.text || '')}</SweepTextWrap>
            </SweepPopup>
          );

        case SweepType.Iframe:
          return (
            <>
              <CloseButton onClick={closeIframeDialog} isFullscreen={false} />
              <Iframe src={group.url} frameBorder="0" allowFullscreen />
            </>
          );

        case SweepType.Image:
          return (
            <>
              <CloseButton onClick={closeImageDialog} isFullscreen={false} />
              <img src={group.image} style={{ width: '100%', height: '100%' }} />
            </>
          );

        case SweepType.GreenScreen:
          return (
            <>
              <CloseButton onClick={closeGreenScreenDialog} isFullscreen={false} />
              {/*@ts-ignore*/}
              <ReactPlayer
                playing={greenScreenPlayer.playing}
                url={greenScreen?.urls}
                playsInline
                width="100%"
                height="100%"
              />
            </>
          );

        case SweepType.Video:
          return (
            <FullScreen
              attrs={{
                id: 'sweep-video-fullscreen',
                styles: { background: 'transparent' },
              }}
              startFullscreen={!!group.fullscreen}
            >
              {({ isFullScreen, toggleFullscreen }) => (
                <>
                  <CloseButton onClick={closeVideoDialog} isFullscreen={isFullScreen} />
                  <FullscreenButton onClick={toggleFullscreen} isFullscreen={isFullScreen} />
                  {/*@ts-ignore*/}
                  <ReactPlayer
                    url={`${group.url}?autoplay=${group.autoPlay ? 1 : 0}`}
                    controls
                    width="100%"
                    height="100%"
                  />
                </>
              )}
            </FullScreen>
          );

        default:
          return <></>;
      }
    },
    [
      closeGreenScreenDialog,
      closeIframeDialog,
      closeImageDialog,
      closeTextDialog,
      closeVideoDialog,
      greenScreen?.urls,
      greenScreenPlayer.playing,
    ],
  );

  return (
    <>
      <div className="sweep-component">
        <div id="page-root"></div>

        {(isTextDialogVisible ||
          isIframeDialogVisible ||
          isVideoDialogVisible ||
          isImageDialogVisible ||
          isGreenScreenDialogVisible) &&
          group && (
            <Popup
              onClick={
                group.type === SweepType.GreenScreen
                  ? () =>
                      setGreenScreenPlayer((prev) => ({
                        ...prev,
                        playing: !greenScreenPlayer.playing,
                      }))
                  : undefined
              }
              style={{
                height: `${group.height}${group.res === 'pixels' ? 'px' : '%'}`,
                width: `${group.width}${group.res === 'pixels' ? 'px' : '%'}`,
                ...(group.type === SweepType.GreenScreen
                  ? {
                      background: 'transparent',
                      ...(group.videoSide === 'right' ? { left: 'auto' } : { right: 'auto' }),
                    }
                  : {}),
              }}
            >
              {renderDialogContent(group)}
            </Popup>
          )}
        {isMusicPlayerVisible && (
          <AudioPlayer
            url={music?.url || ''}
            subtitles={music?.subtitles}
            subtitlesSettings={{
              gutterBottom: group?.musicSubBottom,
              fontFamily: group?.musicSubFontFamily,
              color: group?.musicSubFontColor,
              fontSize: group?.musicSubFontSize,
              opacity: group?.musicSubBgOpacity,
              backgroundColor: group?.musicSubBgColor,
            }}
          />
        )}
      </div>
      {panorama.panoramaOn && panoramas && panoramas?.length > 0 && tour && (
        <Panorama
          panoramas={panoramas}
          awsUrl={awsUrl}
          markMinimap={() => {}}
          panoPose={{ yaw: 0, pitch: 0, hfov: 0 }}
          activePano={panorama.activePano}
          transition={tour.panoramaTransition}
          // isChatGuest={room && socket && socket.connected && !host}
          defaultPanorama={tour.defaultPanorama}
          updatePanoRotation={() => {}}
          openHotspot={() => {}}
          onSceneChange={() => {}}
        />
      )}
    </>
  );
};
